import { useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { GalleryItem } from "@/types/content";

interface LightboxProps {
  items: GalleryItem[];
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export default function Lightbox({ items, index, onClose, onNavigate }: LightboxProps) {
  const isOpen = index !== null;
  const current = index !== null ? items[index] : null;

  useEffect(() => {
    if (!isOpen || index === null) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") {
        onNavigate((index + 1) % items.length);
      }
      if (event.key === "ArrowLeft") {
        onNavigate((index - 1 + items.length) % items.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, index, items.length, onNavigate]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="max-w-4xl overflow-hidden p-0"
      >
        <DialogTitle className="sr-only">{current ? current.title : "Gallery image"}</DialogTitle>

        {current ? (
          <div className="relative bg-primary-950">
            <div className="flex h-[52vh] w-full items-center justify-center bg-primary-950 md:h-[64vh]">
              <img
                src={current.image}
                alt={current.alt}
                title={`${current.title} — Ortho Spine Care`}
                className="max-h-full w-full object-contain"
              />
            </div>

            <button
              type="button"
              onClick={() => onNavigate((index! - 1 + items.length) % items.length)}
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/90 text-foreground-900 transition-colors hover:bg-background-50"
              aria-label="Previous image"
            >
              <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              onClick={() => onNavigate((index! + 1) % items.length)}
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/90 text-foreground-900 transition-colors hover:bg-background-50"
              aria-label="Next image"
            >
              <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
            </button>

            <div className="flex items-center justify-between gap-4 bg-background-50 px-5 py-4">
              <div>
                <p className="font-heading text-base font-semibold text-foreground-950">{current.title}</p>
                <p className="font-label text-xs text-foreground-500">
                  {current.category} · {index! + 1} of {items.length}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 font-label text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-close-line" aria-hidden="true"></i>
                Close
              </button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}